import React, { useEffect, useState } from "react";
import { FaChevronDown, FaChevronUp, FaUserCog, FaCoins, FaArrowLeft, FaTimes } from "react-icons/fa";
// import { MdDarkMode, MdLightMode } from "react-icons/md";
import ResetPasswordModal from "../../Modal/staff/ResetPasswordModal";
import { initAllStores } from "../../utils/cache";
import { errorMessage, sendRequest } from "../../utils/api";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ConfirmModalContext";
import User from "../../models/User";
import { formatDate, normalizeDate, todayDate } from "../../utils/date";
import { useNavigate } from "react-router-dom";
import { fetchAndSetRequests, getUser, payRollColumns, requestTableColumn, totalErnings } from "../../utils/user-helper";
import FullPageSpinner from "../../components/FullPageSpinner";
import { capitalizeFirst } from "../../utils/string";
import { MenuOption, Table, TableMenu } from "../../components/Table";
import Loan from "../../models/Loan";
import Leave from "../../models/Leave";
import RequestDetailsModal from "../../Modal/staff/RequestDetailsModal";
import Payroll from "../../models/Payroll";
import { MAXIMUM_LEAVE_ALLOWED } from "../../constants";
import ProfileAvatar from "../../components/ProfileAvatar";
// import { isEmptyObject } from "../../utils/helper-function";

const StaffProfile: React.FC = () => {
    const LOW_LEAVE_THRESHOLD = 5;
    const [showPerformance, setShowPerformance] = useState(true);
    const [theme, setTheme] = useState("light");
    const [showChatIcon, setShowChatIcon] = useState(true);

    // Modal states
    const [loanModal, setLoanModal] = useState(false);
    const [leaveModal, setLeaveModal] = useState(false);
    const [allowRequest, setAllowRequest] = useState({ loan: true, leave: true });

    const { showToast } = useToast();
    const { openModal, closeModal } = useModal();
    const navigate = useNavigate();

    const [openPasswordModal, setOpenPasswordModal] = useState(false);

    const [user, setUser] = useState<User>({} as User);

    // Loand Data
    const [amount, setAmount] = useState<number>();
    const [month, setMonth] = useState<number>();
    const [reason, setReason] = useState<string>("");
    const [date, setDate] = useState({ start: "", end: "" });

    const [showRequestsList, setShowRequestsList] = useState<RequestType | null>(null);
    const [showPayroll, setShowPayroll] = useState(false);
    const [loans, setLoans] = useState<Loan[]>([]);
    const [leaves, setLeaves] = useState<Leave[]>([]);
    const [payrolls, setPayrolls] = useState<Payroll[]>([]);

    const [requestModal, setRequestModal] = (
        useState<{ type: RequestType, data: Loan | Leave, show: boolean }>({} as any)
    );


    // Loading
    const [loading, setLoading] = useState(false);

    // ---------------------- EFFECT: LOAD ROOMS ----------------------
    useEffect(() => {
        const init = async () => {
            // const cache = await initAllStores();
            const data = await getUser();

            if (!data) {
                return openModal(
                    "Session Expired ⚠️",
                    "We couldn’t find your user information. Please log in again to continue.",
                    () => {
                        closeModal();
                        window.location.href = "/login";
                    },
                    false,
                    "Login Again"
                );
            }

            const { user } = data;
            setUser(user);

            // const allowLoan = hotel?.preference?.loan ?? true;
            // const allowLeave = hotel?.preference?.leave ?? true;

            const allowLeave = true;
            const allowLoan = true;

            const chatBubbleSetting = user?.settings?.find(st => st.name === "chatBubble");
            if (chatBubbleSetting) setShowChatIcon(chatBubbleSetting.value === "show");

            setAllowRequest({ loan: allowLoan, leave: allowLeave })
        };
        init();
    }, []);

    const handleReset = async (newPassword: string) => {
        const cache = await initAllStores();
        setLoading(true);
        try {
            await sendRequest("PUT", "/auth/reset-password/confirm", { newPassword });
            await cache.updateManyBy("data", ["user"], { forceReset: false });
            setOpenPasswordModal(false);
            showToast("Password Reset Successful", { type: "success" });
        } catch (error: any) {
            const err = errorMessage(error.status);
            if (err) openModal(err.title, err.msg, closeModal, false, "Close");
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const staffRequest = async (type: RequestType) => {
        const missingFields: string[] = [];

        if (type === "loan") {
            if (!amount) missingFields.push("Amount");
            if (!month) missingFields.push("Repayment Period");
        } else {
            if (!date.start) missingFields.push("Start Date");
            if (!date.end) missingFields.push("End Date");
        }

        if (missingFields.length > 0) {
            return openModal(
                "Missing Fields",
                `Please fill in: ${missingFields.join(", ")}`,
                closeModal, false, "Close"
            );
        }

        return openModal(
            `Confirm ${capitalizeFirst(type)} Application`,
            `You're about to submit a ${type} application. `
            + "Please ensure your details are correct before proceeding.",
            async () => {
                setLoading(true);

                setLoanModal(false);
                setLeaveModal(false);
                const initData = {
                    id: uuidv4(),
                    createdAt: todayDate(),
                    updatedAt: todayDate(),
                    reason
                }

                const data = {
                    loan: { ...initData, amount, month, },
                    leave: { ...initData, ...date },
                }

                const cache = await initAllStores();
                try {

                    await sendRequest("POST", `/requests/${type}`, data[type]);
                    await cache.saveOne(`${type}s`, data[type])

                    showToast(
                        `${capitalizeFirst(type)} Request Submitted Successfully`, { type: "success" }
                    );
                } catch (error: any) {
                    console.log(error.response.data);
                    if (error.status === 422) {
                        const rx = JSON.parse(error.response.data.error);

                        await cache.updateManyBy("data", ["user"], { leaveUsedDays: rx.usedDays });
                        setUser(prev => ({ ...prev, leaveUsedDays: rx.usedDays }));

                        return openModal(
                            "Leave Request Exceeded ⚠️",
                            `You're requesting more leave days than available:
                            --------------------------------------------
                            Requested Days: ${rx.requestedDays}
                            Remaining Days: ${rx.remainingDays}
                            Used Days: ${rx.usedDays}
                            --------------------------------------------
                            Please reduce the number of days and try again.`,
                            closeModal, false, "Okay"
                        );
                    }
                    showToast(
                        `Failed to Submit ${capitalizeFirst(type)} Request. `
                        + "Please try again.", { type: "error" }
                    );

                } finally {
                    setLoading(false);
                }
            }, true, "Yes, Apply", "Cancel",
        );
    };

    const toggleChatIcon = async (newValue: boolean) => {
        setLoading(true);
        try {
            const cache = await initAllStores();
            const value = newValue ? "show" : "hide";
            // Send update to backend
            await sendRequest("POST", "/users/settings", {
                settings: [{ name: "chatBubble", value }],
            });

            // Update local cache

            const cacheUser = {
                // id: "user",
                userId: user.id,
                settings: user?.settings?.map((st) =>
                    st.name === "chatBubble" ? { ...st, value } : st
                ),
            };

            await cache.updateManyBy("data", ["user"], cacheUser);

            showToast(
                `Floating chat icon ${newValue ? "enabled" : "hidden"} successfully.`,
                { type: "success" }
            );

        } catch (error: any) {
            console.error(error);
            showToast(
                `Failed to ${newValue ? "enable" : "hide"} the floating chat icon. Please try again.`,
                { type: "error" }
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchRequests = async (requestType: RequestType) => {
        setLoading(true);
        try {
            // const req = await sendRequest("GET", `/requests/${requestType}`);
            if (requestType === "loan") {
                await fetchAndSetRequests<Loan>("/users/loan", setLoans, "loan");
                setShowRequestsList("loan")
            }
            else {
                await fetchAndSetRequests<Leave>("/users/leave", setLeaves, "leave");
                setShowRequestsList("leave")
            }

        } catch (err) {
            console.log(err)
            showToast(
                "Unable to fetch loan requests at the moment. "
                + "Please try again later.", { type: "error" }
            );
        } finally {
            setLoading(false);
        }
    }

    const fetchPayroll = async () => {
        setLoading(true);
        try {
            const res = await sendRequest("GET", "/users/payrolls");
            setPayrolls(
                res.map((pl: Payroll) => ({
                    ...pl,
                    totalDeductions: pl.totalDeductions.toLocaleString(),
                    totalEarnings: pl.totalEarnings.toLocaleString(),
                    netPay: pl.netPay.toLocaleString(),
                    createdAt: formatDate(pl.createdAt),
                })));

            setShowPayroll(true);
        } catch (err) {
            console.log(err)
            showToast(
                "Unable to fetch loan requests at the moment. "
                + "Please try again later.", { type: "error" }
            );
        } finally {
            setLoading(false);
        }
    }

    const isWeekend = (dateString: string): boolean => {
        const day = new Date(`${dateString}T00:00:00`).getDay();
        return day === 0 || day === 6; // Sunday or Saturday
    };

    if (!user) return null;

    const leaveQuota: number = user.leaveQuota ?? parseInt(MAXIMUM_LEAVE_ALLOWED);
    const leaveRemaining = leaveQuota - (user.leaveUsedDays ?? 0);

    return (
        <div className="p-6 relative bg-gray-50">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    {/* Back arrow only */}
                    <button
                        type="button"
                        onClick={() => {
                            if (window.history.length > 1) {
                                window.history.back();
                            } else {
                                navigate("/dashboard");
                            }
                        }}
                        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                    >
                        {FaArrowLeft({
                            size: 13,
                            className: "",
                        })}
                    </button>

                    {/* Header title with icon */}
                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                        <span className="text-blue-600" >{FaUserCog({})}</span> My Profile
                    </h2>
                </div>

                <FullPageSpinner show={loading} />

                <ResetPasswordModal
                    isOpen={openPasswordModal}
                    showCloseBtn={true}
                    onClose={() => setOpenPasswordModal(false)}
                    onReset={handleReset}
                />

                {/* Edit button */}
                <button
                    onClick={() => navigate(
                        `/staffs/update?staffId=${user.id}`, { state: user }
                    )}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                    Edit Profile
                </button>
            </div>

            {/* Profile Card */}
            <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100">
                {/* Basic Info */}
                <div className="flex items-center space-x-4 mb-6">
                    <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-2xl font-bold">
                        <ProfileAvatar
                            photoUrl={user?.photoUrl}
                            firstName={user?.firstName}
                            size="w-20 h-20"
                            className="border-2 border-white shadow-sm"
                        />
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-gray-800">
                            {user.firstName + " " + user.lastName}
                        </h3>
                        <p className="text-gray-500 capitalize">{user.portfolio}</p>

                        {allowRequest.loan && <p className="text-sm text-gray-500"> Leave Balance: {" "}
                            <span className={`font-semibold ${leaveRemaining < LOW_LEAVE_THRESHOLD ? "text-red-600" : "text-green-600"}`}>
                                {leaveRemaining} days
                            </span>
                        </p>}
                        <p className="text-sm text-gray-400">
                            Joined: {user.startDate ? formatDate(user.startDate) : ""}
                        </p>
                    </div>
                </div>

                {/* Employment Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <h4 className="text-gray-700 font-medium mb-2">Employment Details</h4>
                        {user.staffId && <p><span className="font-semibold">Staff Id:</span> <span className="uppercase">{user.staffId}</span></p>}
                        <p><span className="font-semibold">Email:</span> {user.email}</p>
                        <p><span className="font-semibold">Phone:</span> {user.phone}</p>
                        <p><span className="font-semibold">Address:</span> {user.address}</p>
                    </div>

                    <div>
                        <h4 className="text-gray-700 font-medium mb-2">Job Information</h4>
                        <p><span className="font-semibold">Position:</span> <span className="capitalize">{user.portfolio}</span></p>
                        {/* <p><span className="font-semibold">Department:</span> Front Office</p> */}
                        <p><span className="font-semibold">Salary:</span>
                            {user?.salary ? `₦${totalErnings(user)?.toLocaleString()}/month` : ""}
                        </p>
                        {/* <p><span className="font-semibold">Payment Mode:</span> Bank Transfer</p> */}
                    </div>
                </div>

                {/* Action Center */}
                <div className="border-t pt-4">
                    <h4 className="text-gray-700 font-semibold mb-3 flex items-center gap-2">
                        ⚙️ Action Center
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                        {allowRequest.loan && <button
                            onClick={() => setLoanModal(true)}
                            className="p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 font-medium transition"
                        >
                            Apply for Loan
                        </button>}
                        <button
                            onClick={() => setLeaveModal(true)}
                            className="relative p-3 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg text-green-700 font-medium transition flex items-center justify-center"
                        >
                            <span>Apply for Leave</span>

                            {/* <span className="absolute right-3 text-sm bg-green-200 px-2 py-1 rounded-full">
                                {7} left
                            </span> */}
                        </button>

                        <button
                            onClick={() => setOpenPasswordModal(true)}
                            className="p-3 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 rounded-lg text-yellow-700 font-medium transition">
                            Reset Password
                        </button>
                    </div>

                    {/* Settings */}
                    <div className="space-y-3 mb-4">
                        <div>
                            <label className="font-medium text-gray-700 mr-2">Theme:</label>
                            <select
                                value={theme}
                                onChange={(e) => setTheme(e.target.value)}
                                className="border p-2 rounded-md"
                            >
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                                {/* <option value="auto">Auto</option> */}
                            </select>
                            {/* {theme === "dark" ? (
                                <span className="inline ml-2 text-gray-600">{MdDarkMode({})}</span>
                            ) : (
                                <span className="inline ml-2 text-yellow-500">{MdLightMode({})}</span>
                            )} */}
                        </div>

                        <div className="flex items-center">
                            <input
                                className="cursor-pointer"
                                type="checkbox"
                                checked={showChatIcon}
                                onChange={(e) => {
                                    const newValue = e.target.checked; // the new checked value
                                    setShowChatIcon(newValue);
                                    toggleChatIcon(newValue);
                                }}
                            />
                            &nbsp;&nbsp;
                            <label className="text-gray-700">Show Floating Chat Icon</label>
                        </div>
                    </div>

                    {/* Performance */}
                    <button
                        onClick={() => setShowPerformance(!showPerformance)}
                        className="flex items-center text-blue-600 font-medium hover:underline"
                    >
                        {showPerformance ? (
                            <>
                                Collapse Panel <span className="ml-1" >{FaChevronUp({})}</span>
                            </>
                        ) : (
                            <>
                                Expand Performance Panel <span className="ml-1">{FaChevronDown({})}</span>
                            </>
                        )}
                    </button>

                    {showPerformance && (
                        <div className="mt-4 bg-gray-50 rounded-lg p-5 border border-gray-200">
                            <h4 className="text-gray-700 font-semibold mb-3">Performance Snapshot</h4>

                            {/* Performance Index & Coins */}
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-6">
                                {/* Index */}
                                <div className="flex items-center gap-4">
                                    <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-3xl font-bold">
                                        {30}%
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-700 text-lg">Performance Index</p>
                                        <p className="text-sm text-gray-500">Excellent this month</p>
                                    </div>
                                </div>

                                {/* Coins */}
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 text-2xl">
                                        {FaCoins({})}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-700 text-lg">Performance Coins</p>
                                        <p className="text-sm text-gray-500">
                                            Earned: <span className="font-semibold text-yellow-600">+{20}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <p>
                                    <span className="font-semibold">Attendance:</span> {"20/5"}
                                </p>
                                <p>
                                    <span className="font-semibold">Last Clock-in:</span> {"8:45 AM"}
                                </p>
                                <p>
                                    <span className="font-semibold">Evaluation:</span> {"92% (Excellent)"}
                                </p>
                                <p>
                                    <span className="font-semibold">Supervisor Note:</span> {"Reliable and consistent."}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className="mt-6 flex flex-wrap gap-3">
                    {/* {allowRequest.payroll && ( */}
                    <button
                        className="px-4 py-2 bg-purple-500 text-white font-semibold rounded-lg 
                            hover:bg-purple-600 hover:scale-105 transition transform shadow-md"
                        onClick={() => {
                            fetchPayroll();
                        }}
                    >
                        Payroll History
                    </button>
                    {/* )} */}

                    {allowRequest.loan && (
                        <button
                            className="px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg 
                 hover:bg-blue-600 hover:scale-105 transition transform shadow-md"
                            onClick={() => {
                                setShowPayroll(false);
                                setShowRequestsList("loan");
                                fetchRequests("loan")
                            }}
                        >
                            Loan History
                        </button>
                    )}

                    {allowRequest.leave && (
                        <button
                            className="px-4 py-2 bg-green-500 text-white font-semibold rounded-lg 
                 hover:bg-green-600 hover:scale-105 transition transform shadow-md"
                            onClick={() => {
                                setShowPayroll(false);
                                setShowRequestsList("leave");
                                fetchRequests("leave")
                            }}
                        >
                            Leave History
                        </button>
                    )}
                </div>
            </div>

            {(showRequestsList || showPayroll) && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fadeIn">
                <div className="relative w-[90%] max-w-3xl bg-white rounded-lg shadow-xl animate-scaleIn overflow-hidden">

                    <FullPageSpinner show={loading} />

                    {requestModal.show && (
                        <RequestDetailsModal
                            show={requestModal.show}
                            onClose={() => setRequestModal(prev => ({ ...prev, show: false }))}
                            type={requestModal.type}
                            data={requestModal.data}
                        />
                    )}
                    {/* Top Accent Bar */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-green-600"></div>

                    {/* Header */}
                    <div className="flex items-center justify-between border-b p-4 bg-gray-50">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <span
                                className="w-3.5 h-3.5 rounded-full bg-green-500 shadow-sm"
                            ></span>

                            {showRequestsList === "loan" && "Loan Request History"}
                            {showRequestsList === "leave" && "Leave Request History"}
                            {showPayroll && "Payroll  History"}
                        </h2>

                        <button
                            className="text-gray-500 hover:text-red-500 transition"
                            onClick={() => {
                                setShowRequestsList(null);
                                setShowPayroll(false);
                            }}
                        >
                            {FaTimes({ size: 18 })}
                        </button>
                    </div>

                    {/* Table Content */}
                    <div className="p-4 max-h-[70vh] overflow-y-auto">
                        {showPayroll && (
                            <Table
                                columns={payRollColumns("profile")}
                                data={payrolls}
                                rowsPerPage={10}
                            // tableMenu={(row: Payroll, closeMenu) => (
                            //     <TableMenu
                            //         row={row}
                            //         options={[
                            //             {
                            //                 label: "View Details",
                            //                 color: "text-gray-700",
                            //                 onClick: (leave: Leave) => {
                            //                     setRequestModal({
                            //                         type: "leave", data: leave, show: true
                            //                     });
                            //                 },
                            //             },

                            //         ].filter(Boolean) as MenuOption[]}
                            //         closeMenu={closeMenu}
                            //     />
                            // )}
                            />
                        )}

                        {showRequestsList === "loan" && (<Table
                            columns={requestTableColumn("loan", false)}
                            data={loans}
                            rowsPerPage={10}
                            tableMenu={(row: Loan, closeMenu) => (
                                <TableMenu
                                    row={row}
                                    options={[
                                        {
                                            label: "View Details",
                                            color: "text-gray-700",
                                            onClick: (loan: Leave) => {
                                                setRequestModal({
                                                    type: "loan", data: loan, show: true
                                                });
                                            },
                                        },

                                    ].filter(Boolean) as MenuOption[]}
                                    closeMenu={closeMenu}
                                />
                            )}
                        />)}

                        {showRequestsList === "leave" && (
                            <Table
                                columns={requestTableColumn("leave", false)}
                                data={leaves}
                                rowsPerPage={10}
                                tableMenu={(row: Leave, closeMenu) => (
                                    <TableMenu
                                        row={row}
                                        options={[
                                            {
                                                label: "View Details",
                                                color: "text-gray-700",
                                                onClick: (leave: Leave) => {
                                                    setRequestModal({
                                                        type: "leave", data: leave, show: true
                                                    });
                                                },
                                            },

                                        ].filter(Boolean) as MenuOption[]}
                                        closeMenu={closeMenu}
                                    />
                                )}
                            />
                        )}

                    </div>

                    {/* Footer */}
                    {/* <div className="text-center text-gray-400 text-xs py-3 border-t bg-gray-50">
          © {new Date().getFullYear()} Wiventory. All rights reserved.
        </div> */}
                </div>
            </div>}


            {/* MODALS */}
            {loanModal && (
                <Modal title="Apply for Loan" onClose={() => setLoanModal(false)}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-gray-700 font-medium mb-1">Loan Amount</label>
                            <input
                                type="number"
                                placeholder="Enter amount"
                                className="w-full border p-2 rounded-md"
                                onChange={(e) => setAmount(parseFloat(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 mb-1">Repayment Period (months)</label>
                            <input
                                type="number"
                                className="w-full border p-2 rounded-md"
                                placeholder="e.g. 6"
                                onChange={(e) => setMonth(parseFloat(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1">Reason / Description</label>
                            <textarea
                                placeholder="Why you need this loan..."
                                className="w-full border p-2 rounded-md"
                                rows={3}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => staffRequest("loan")}
                            className="w-full bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition"
                        >
                            Submit Loan Request
                        </button>
                    </div>
                </Modal>
            )}

            {leaveModal && (
                <Modal title="Apply for Leave" onClose={() => setLeaveModal(false)}>
                    <div className="space-y-4">

                        <div>
                            <label className="block text-gray-700 font-medium mb-1">
                                Leave Start Date
                            </label>

                            <input
                                type="date"
                                className="w-full border p-2 rounded-md"
                                value={date.start}
                                min={normalizeDate(new Date()).toISOString().split("T")[0]}
                                onChange={(e) => {
                                    const start = e.target.value;

                                    if (isWeekend(start)) {
                                        showToast(
                                            "Saturdays and Sundays cannot be selected for leave.",
                                            { type: "error" }
                                        );
                                        return;
                                    }

                                    const selectedDate = normalizeDate(start);
                                    const today = normalizeDate(new Date());
                                    const endDate = date.end
                                        ? normalizeDate(date.end)
                                        : null;

                                    if (selectedDate < today) {
                                        showToast("Start date cannot be in the past.", {
                                            type: "error",
                                        });
                                        return;
                                    }

                                    if (endDate && selectedDate > endDate) {
                                        showToast("Start date cannot exceed end date.", {
                                            type: "error",
                                        });
                                        return;
                                    }

                                    setDate({ ...date, start });
                                }}
                            />
                        </div>


                        <div>
                            <label className="block text-gray-700 font-medium mb-1">
                                Leave End Date
                            </label>

                            <input
                                type="date"
                                className="w-full border p-2 rounded-md"
                                value={date.end}
                                min={date.start || normalizeDate(new Date()).toISOString().split("T")[0]}
                                onChange={(e) => {
                                    const end = e.target.value;

                                    if (!date.start) {
                                        showToast("Please select a start date first.", {
                                            type: "error",
                                        });
                                        return;
                                    }

                                    if (isWeekend(end)) {
                                        showToast(
                                            "Saturdays and Sundays cannot be selected for leave.",
                                            { type: "error" }
                                        );
                                        return;
                                    }

                                    const startDate = normalizeDate(date.start);
                                    const endDate = normalizeDate(end);

                                    if (endDate < startDate) {
                                        showToast("End date cannot be before start date.", {
                                            type: "error",
                                        });
                                        return;
                                    }

                                    setDate({ ...date, end });
                                }}
                            />
                        </div>


                        <div>
                            <label
                                className="block text-gray-700 font-medium mb-1">Reason</label>
                            <textarea
                                placeholder="Reason for leave..."
                                onChange={(e) => setReason(e.target.value)}
                                className="w-full border p-2 rounded-md"
                                rows={3}
                            />
                        </div>
                        <button
                            onClick={() => {
                                // setDate({ start: "", end: "" });
                                staffRequest("leave");
                            }}
                            className="w-full bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition"
                        >
                            Submit Leave Request
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// ✅ Reusable modal component
const Modal = ({ title, onClose, children }: any) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 w-[90%] md:w-[450px]">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                <button onClick={onClose} className="text-gray-500 hover:text-red-500 text-lg font-bold">
                    {FaTimes({})}
                </button>
            </div>
            {children}
            {/* <div className="mt-5 flex justify-end">
                <button
                    onClick={onClose}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                    Close
                </button>
            </div> */}
        </div>
    </div>
);

export default StaffProfile;